// UserMenu — avatar trigger + dropdown in the redesigned top nav.
//
// Trigger mirrors `.topnav__user` (avatar + chevron, chevron drops on mobile).
// The dropdown mirrors the design's `UserMenu` (320px card, identity header,
// item rows, error-toned Log out). The design's "Settings" row is replaced by
// Offboarding + Attestations — the two prod routes that don't have a home in
// the slimmed 5-item nav — plus the command line tool's setup page and the
// member's passkeys.

import {
  Avatar,
  Box,
  Icon,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  FileSignature,
  KeyRound,
  LogOut,
  SquareTerminal,
  UserMinus,
} from "lucide-react";
import { TbChevronDown } from "react-icons/tb";
import { useMemberLogout } from "modules/members/auth";
import type { MFMember } from "state/members/member";
import Linker from "ui/Linker";

// `--shadow-overlay` from the design's colors_and_type.css. No exact theme
// token, so inline it verbatim.
const SHADOW_OVERLAY =
  "0px 4px 6px -2px rgba(16,24,40,0.03), 0px 12px 16px -4px rgba(16,24,40,0.08)";

const itemStyles = {
  gap: "12px",
  px: "12px",
  py: "10px",
  borderRadius: "component.lg", // 8px
  fontSize: "md", // 14px
  fontWeight: 500,
  color: "neutral.900",
  _hover: { textDecoration: "none", bg: "neutral.100" },
  _focus: { bg: "neutral.100" },
} as const;

const UserMenu = ({ me }: { me: MFMember }) => {
  const logout = useMemberLogout();
  const name = me.name || [me.firstName, me.lastName].filter(Boolean).join(" ");

  return (
    <Menu placement="bottom-end" offset={[0, 8]} autoSelect={false}>
      <MenuButton
        type="button"
        aria-label="User menu"
        display="inline-flex"
        alignItems="center"
        gap="1xs" // 6px
        // .topnav__user: 4px 8px 4px 4px desktop, 4px all round on mobile.
        sx={{
          padding: "4px 8px 4px 4px",
          "@media (max-width: 768px)": { padding: "4px" },
        }}
        borderRadius="component.lg" // 8px
        transition="background 120ms"
        _hover={{ bg: "neutral.100" }}
        _active={{ bg: "neutral.100" }}
      >
        <Box display="inline-flex" alignItems="center" gap="1xs">
          <Avatar
            src={me.imageUrl}
            name={name}
            boxSize="32px" // .avatar--md
            fontSize="12px"
          />
          <Icon
            as={TbChevronDown}
            boxSize="14px"
            color="neutral.700"
            // chevron drops on mobile
            display={{ base: "none", md: "block" }}
          />
        </Box>
      </MenuButton>

      <MenuList
        p={0}
        w="320px"
        bg="neutral.0"
        border="1px solid"
        borderColor="neutral.200"
        borderRadius="component.3xl" // 14px
        boxShadow={SHADOW_OVERLAY}
        overflow="hidden"
      >
        {/* Identity header */}
        <Box display="flex" alignItems="center" gap="12px" p="16px">
          <Avatar
            src={me.imageUrl}
            name={name}
            boxSize="40px" // .avatar--lg
            fontSize="14px"
          />
          <Box minW={0} flex={1}>
            <Text
              fontSize="15px"
              fontWeight={600}
              color="neutral.950"
              lineHeight={1.2}
              noOfLines={1}
            >
              {name}
            </Text>
            <Text fontSize="smd" color="neutral.700" mt="2px" noOfLines={1}>
              {me.email}
            </Text>
          </Box>
        </Box>

        <VStack spacing={0} align="stretch" px="8px" pb="4px">
          <MenuItem as={Linker} to="/members/offboarding" {...itemStyles}>
            <Icon as={UserMinus} boxSize="18px" color="neutral.800" />
            Offboarding
          </MenuItem>
          <MenuItem as={Linker} to="/members/attestations" {...itemStyles}>
            <Icon as={FileSignature} boxSize="18px" color="neutral.800" />
            Attestations
          </MenuItem>
          <MenuItem as={Linker} to="/cli/setup" {...itemStyles}>
            <Icon as={SquareTerminal} boxSize="18px" color="neutral.800" />
            Command line tool
          </MenuItem>
          <MenuItem as={Linker} to="/members/passkeys" {...itemStyles}>
            <Icon as={KeyRound} boxSize="18px" color="neutral.800" />
            Passkeys
          </MenuItem>
        </VStack>

        <MenuDivider m={0} borderColor="neutral.200" />

        <Box px="8px" py="8px">
          <MenuItem
            onClick={logout}
            {...itemStyles}
            color="error.700"
            _hover={{ textDecoration: "none", bg: "error.50" }}
            _focus={{ bg: "error.50" }}
          >
            <Icon as={LogOut} boxSize="18px" color="error.600" />
            Log out
          </MenuItem>
        </Box>
      </MenuList>
    </Menu>
  );
};

export default UserMenu;
